import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { INFO } from "@/data/site";
import { useLang } from "@/i18n";

/**
 * Portfolio grid in classic Instagram-feed style (3×N square tiles, tight gaps).
 * Add more items by dropping a JPG in /public/portfolio/ and appending to ITEMS.
 */
const ITEMS = [
  { src: "/portfolio/p1.jpg", alt: "Rosa Polka-Dot Nägel" },
  { src: "/portfolio/p5.jpg", alt: "Zarte French Pediküre im Fußbad" },
  { src: "/portfolio/p2.jpg", alt: "Türkis French mit 3D-Blüte" },
  { src: "/portfolio/p3.jpg", alt: "Klassisches Rot – kurzes Quadrat" },
  { src: "/portfolio/p6.jpg", alt: "French Pediküre – natürlich & elegant" },
  { src: "/portfolio/p4.jpg", alt: "French mit floralem Nail-Art" },
  { src: "/before-after/nachher_1.jpg", alt: "Nachher – Naturnagelverstärkung" },
  { src: "/before-after/nachher_2.jpg", alt: "Lange French-Verlängerung" },
  { src: "/before-after/nachher_3.jpg", alt: "French Pediküre mit Shimmer" },
];

const Lightbox = ({ items, idx, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      data-testid="portfolio-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-foreground/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        aria-label="Schließen"
        className="absolute top-5 right-5 md:top-8 md:right-8 h-11 w-11 rounded-full bg-background/10 hover:bg-background/20 text-background flex items-center justify-center transition-colors"
      >
        <X strokeWidth={1.5} size={20} />
      </button>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        data-testid="lightbox-prev"
        aria-label="Vorheriges Bild"
        className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-background/10 hover:bg-background/20 text-background flex items-center justify-center transition-colors"
      >
        <ChevronLeft strokeWidth={1.5} size={22} />
      </button>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        data-testid="lightbox-next"
        aria-label="Nächstes Bild"
        className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-background/10 hover:bg-background/20 text-background flex items-center justify-center transition-colors"
      >
        <ChevronRight strokeWidth={1.5} size={22} />
      </button>

      <motion.img
        key={items[idx].src}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        src={items[idx].src}
        alt={items[idx].alt}
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
        className="max-h-[85vh] max-w-full object-contain shadow-2xl"
      />

      <div className="absolute bottom-6 left-0 right-0 text-center text-background/70 text-xs tracking-[0.25em] tabular-nums" data-testid="lightbox-counter">
        {String(idx + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
      </div>
    </motion.div>
  );
};

export const Portfolio = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const { t } = useLang();
  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(() => setOpenIdx((i) => (i - 1 + ITEMS.length) % ITEMS.length), []);
  const next = useCallback(() => setOpenIdx((i) => (i + 1) % ITEMS.length), []);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="bg-background border-t border-border">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12 md:mb-16">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("portfolio.eyebrow")}</span>
              <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight mt-4">{t("portfolio.title")}</h2>
              <p className="mt-5 max-w-md text-foreground/60 leading-relaxed">
                {t("portfolio.body")}
              </p>
            </div>
            <a
              href={INFO.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="portfolio-instagram-link"
              className="inline-flex items-center gap-2 border border-primary text-primary px-6 py-3 text-sm tracking-wide hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              {t("portfolio.follow")} <ExternalLink size={14} />
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="grid grid-cols-3 gap-1 md:gap-1.5" data-testid="portfolio-grid">
            {ITEMS.map((it, i) => (
              <button
                type="button"
                key={it.src}
                onClick={() => setOpenIdx(i)}
                data-testid={`portfolio-tile-${i}`}
                className="group relative aspect-square overflow-hidden bg-muted"
                aria-label={`Bild öffnen: ${it.alt}`}
              >
                <img
                  src={it.src}
                  alt={it.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/25 transition-colors duration-300" />
              </button>
            ))}
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {openIdx !== null && (
          <Lightbox
            items={ITEMS}
            idx={openIdx}
            onClose={close}
            onPrev={prev}
            onNext={next}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
